import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, Lock, Zap, ShieldCheck, Headphones, Cloud, Server, HardDrive, Database, Mail, Layers, Globe, Building, Lock as LockIcon } from 'lucide-react';
import { pricingPlans } from '../mock/mock';
import { useQuote } from './QuoteContext';

const iconMap = { Cloud, Server, HardDrive, Database, Mail, Layers, Globe, Building, Lock: LockIcon };

const PricingCard = ({ plan, i }) => {
  const { openQuote } = useQuote();
  const Icon = iconMap[plan.icon] || Cloud;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
      whileHover={{ y: -6 }}
      className={`relative rounded-2xl p-6 pt-8 transition-all group ${
        plan.popular
          ? 'bg-gradient-to-b from-sky-50 to-white border-2 border-sky-400 shadow-2xl shadow-sky-500/25 lg:scale-[1.03] mt-4'
          : 'bg-white border border-slate-200 hover:border-sky-300 hover:shadow-xl'
      }`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-white text-[11px] font-semibold flex items-center gap-1 shadow-md whitespace-nowrap">
          <Sparkles size={12} /> Most Popular
        </div>
      )}

      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center shadow-lg mb-4 mx-auto`}>
        <Icon className="text-white" size={24} />
      </div>

      <h3 className="text-lg font-bold text-slate-900 text-center">{plan.name}</h3>

      <div className="my-6 flex justify-center">
        <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center bg-gradient-to-br ${plan.color} shadow-xl group-hover:scale-105 transition-transform`}>
          <Lock className="text-white" size={32} strokeWidth={2.5} />
        </div>
      </div>

      <div className="text-center">
        <div className={`text-xl font-black bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>Unlock Price</div>
        <div className="text-xs text-slate-500 mt-1">Get exclusive pricing tailored for you.</div>
      </div>

      <ul className="mt-5 space-y-2">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check size={16} className="text-sky-500 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => openQuote({ title: `Get Quote: ${plan.name}`, subtitle: `Tell us about your ${plan.name.toLowerCase()} needs and we'll send you a personalized quote within 24 hours.`, service: plan.name })}
        className={`mt-6 w-full py-2.5 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all bg-gradient-to-r ${plan.color} text-white hover:shadow-lg hover:-translate-y-0.5`}
      >
        <Lock size={14} /> Unlock &amp; Get Quote <ArrowRight size={14} />
      </button>
      <p className="mt-2 text-[11px] text-slate-500 text-center flex items-center justify-center gap-1">
        <Lock size={10} /> No credit card required
      </p>
    </motion.div>
  );
};

const Pricing = () => {
  return (
    <section id="pricing" className="section-padding section-warm relative">
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="eyebrow-light mb-4"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Transparent Pricing</div>
          <h2 className="section-title-dark">Explore Our Hosting <span className="gradient-text">Pricing</span></h2>
          <p className="section-subtitle-dark mx-auto mt-4">
            Every plan for every workload — Shared, WordPress, Zimbra, VPS, Cloud, Dedicated, Colocation and more. Click any plan to unlock live pricing tailored for you.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10">
          {[{ Icon: Zap, label: 'Instant Setup' }, { Icon: ShieldCheck, label: '99.99% Uptime' }, { Icon: Headphones, label: '24/7 Support' }].map((f) => (
            <div key={f.label} className="flex items-center gap-2 text-slate-700">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md shadow-sky-500/25">
                <f.Icon className="text-white" size={16} />
              </div>
              <span className="font-semibold text-sm">{f.label}</span>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
          {pricingPlans.map((plan, i) => (<PricingCard key={plan.id} plan={plan} i={i} />))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
